/**
 * DMS items: models, fabrics, elements and prices share one collection, `type`
 * + free-form `data`.
 */
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { api, fetchBlob, request } from '@/shared/api/client';
import { getToken } from '@/features/auth/session';

export type DmsItemType = 'model' | 'fabric' | 'element' | 'price';

/** Free-form `data` of a DMS item, when a screen has no schema for it. */
export const anyDmsData = z.record(z.string(), z.unknown());
export type AnyDmsData = z.infer<typeof anyDmsData>;

/**
 * A DMS item with its `data` validated by the schema the screen provides
 * (models, fabrics and elements declare theirs next to their form). The API
 * adds fields of its own on the item (a fabric's `displayAvailability`, the
 * timestamps), kept as they come.
 */
export const dmsItemSchema = <Data extends AnyDmsData>(data: z.ZodType<Data>) =>
  z
    .object({
      id: z.string(),
      type: z.string(),
      data: z.preprocess((value) => value ?? {}, data),
    })
    .loose();

export interface DmsItem<Data extends AnyDmsData = AnyDmsData> {
  id: string;
  type: string;
  data: Data;
  [key: string]: unknown;
}

export const dmsPageSchema = <Data extends AnyDmsData>(data: z.ZodType<Data>) =>
  z.object({
    data: z.array(dmsItemSchema(data)),
    nextCursor: z.string().nullish(),
  });

export interface DmsPage<Data extends AnyDmsData = AnyDmsData> {
  data: DmsItem<Data>[];
  nextCursor?: string | null | undefined;
}

export const dmsItemsQueryKey = (type: DmsItemType) =>
  ['dms-items', type] as const;

/** GET dms-item?type=: every item of a type (`fields` narrows the projection). */
export const dmsItemsQueryOptions = <Data extends AnyDmsData>(
  type: DmsItemType,
  data: z.ZodType<Data>,
  fields?: string,
) =>
  queryOptions({
    queryKey: fields
      ? ([...dmsItemsQueryKey(type), fields] as const)
      : dmsItemsQueryKey(type),
    queryFn: () =>
      api.get<DmsItem<Data>[]>('dms-item', {
        query: { type, ...(fields ? { fields } : {}) },
        schema: z.array(dmsItemSchema(data)),
      }),
    retry: false,
  });

export const createDmsItem = <Data extends AnyDmsData>(
  item: { type: DmsItemType; data: Data },
  data: z.ZodType<Data>,
) => api.post<DmsItem<Data>>('dms-item', item, { schema: dmsItemSchema(data) });

export const updateDmsItem = <Data extends AnyDmsData>(
  id: string,
  item: { type?: string; data: Data },
  data: z.ZodType<Data>,
) =>
  api.put<DmsItem<Data>>(`dms-item/${encodeURIComponent(id)}`, item, {
    schema: dmsItemSchema(data),
  });

export const deleteDmsItem = (id: string) =>
  api.delete<unknown>(`dms-item/${encodeURIComponent(id)}`);

/** POST dms-item/:id/picture/:type?code=: answers with the relative asset `url`. */
export const uploadDmsItemPicture = async (
  id: string,
  file: File,
  pictureType: string,
  code: string,
): Promise<{ url: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  const headers = new Headers();
  const token = getToken();
  if (token) headers.set('Authorization', token);
  const response = await fetch(
    `/api/dms-item/${encodeURIComponent(id)}/picture/${encodeURIComponent(pictureType)}?code=${encodeURIComponent(code)}`,
    { method: 'POST', headers, body: formData, credentials: 'same-origin' },
  );
  if (!response.ok)
    throw new Error(`Upload failed with status ${response.status}`);
  return (await response.json()) as { url: string };
};

export const deleteDmsItemPicture = (
  id: string,
  pictureType: string,
  code: string,
) =>
  request(
    `dms-item/${encodeURIComponent(id)}/picture/${encodeURIComponent(pictureType)}?code=${encodeURIComponent(code)}`,
    { method: 'DELETE' },
  );

/**
 * Sends a chosen picture, or deletes a removed one,
 * and answers with the URL to store on the item (null after a removal).
 */
export const syncDmsItemPicture = async (
  id: string,
  pictureType: string,
  code: string,
  file: File | null,
): Promise<string | null> => {
  if (file) {
    const { url } = await uploadDmsItemPicture(id, file, pictureType, code);
    return url;
  }
  await deleteDmsItemPicture(id, pictureType, code);
  return null;
};

/** PUT dms-item/bulk-replace?type=: replaces every item of the type with the CSV rows. */
export const bulkReplaceDmsItems = (
  type: DmsItemType,
  items: { type: DmsItemType; data: AnyDmsData }[],
) => api.put<unknown>('dms-item/bulk-replace', items, { query: { type } });

/** GET dms-item/:type/export: the CSV file of every item of the type. */
export const exportDmsCsv = (type: 'fabric' | 'price'): Promise<Blob> =>
  fetchBlob(`dms-item/${type}/export`);

const logSchema = z.object({
  type: z.string().nullish(),
  filename: z.string().nullish(),
  createdAt: z.string().nullish(),
});
export type UploadLog = z.infer<typeof logSchema>;

/** GET log: the most recent log of a type (sorted by date, limit 1). */
export const lastLogQueryOptions = (type: string, refresh: number) =>
  queryOptions({
    queryKey: ['logs', type, refresh] as const,
    queryFn: async () =>
      (
        await api.get<UploadLog[]>('log', {
          query: {
            query: JSON.stringify({ type }),
            options: JSON.stringify({ sort: { createdAt: 'desc' }, limit: 1 }),
          },
          schema: z.array(logSchema),
        })
      )[0] ?? null,
    retry: false,
  });

export const createLog = (log: { type: string; filename: string }) =>
  api.post<unknown>('log', log);
